import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { ErrorBoundary } from '@/components/common/ErrorBoundary'
import { api } from '@/services/api'
import { useAppDispatch } from '@/store/hooks'
import { loginUser } from '@/store/thunks/authThunks'
import { toast } from 'sonner'

// Component that throws JavaScript errors
const ErrorThrower: React.FC<{ shouldThrow: boolean; errorType: string }> = ({ 
  shouldThrow, 
  errorType 
}) => {
  if (shouldThrow) {
    switch (errorType) {
      case 'reference':
        // @ts-ignore - Intentionally cause a reference error
        return <div>{undefinedVariable.toString()}</div>
      case 'type':
        // @ts-ignore - Intentionally cause a type error
        return <div>{null.toString()}</div>
      case 'custom':
        throw new Error('This is a custom error for testing ErrorBoundary')
      default:
        throw new Error('Generic test error')
    }
  }
  return <div className="text-green-600">✅ No error - component rendered successfully</div>
}

// Component for testing async errors
const AsyncErrorTester: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const dispatch = useAppDispatch()

  const testApiError = async (errorType: 'network' | 'server' | 'auth') => {
    setIsLoading(true)
    setError(null)

    try {
      switch (errorType) {
        case 'network':
          // This will fail if backend is not running
          await api.get('/nonexistent-endpoint')
          break
        case 'server':
          // This should return a 404 or validation error
          await api.post('/auth/login', { invalid: 'data' })
          break
        case 'auth':
          // Test authentication error
          await dispatch(loginUser({ 
            email: 'invalid@email.com', 
            password: 'wrongpassword' 
          })).unwrap()
          break
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred')
      toast.error(`${errorType.toUpperCase()} Error: ${err.message}`)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Async Error Testing</h3>
      
      <div className="flex gap-2 flex-wrap">
        <Button 
          onClick={() => testApiError('network')}
          disabled={isLoading}
          variant="destructive"
        >
          {isLoading ? 'Testing...' : 'Test Network Error'}
        </Button>
        
        <Button 
          onClick={() => testApiError('server')}
          disabled={isLoading}
          variant="destructive"
        >
          {isLoading ? 'Testing...' : 'Test Server Error'}
        </Button>
        
        <Button 
          onClick={() => testApiError('auth')}
          disabled={isLoading}
          variant="destructive"
        >
          {isLoading ? 'Testing...' : 'Test Auth Error'}
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          <strong>Error:</strong> {error}
        </div>
      )}
    </div>
  )
}

const ErrorTestPage: React.FC = () => {
  const [jsErrorType, setJsErrorType] = useState<string>('')
  const [showJsError, setShowJsError] = useState(false)

  const triggerJsError = (errorType: string) => {
    setJsErrorType(errorType)
    setShowJsError(true)
  }

  const resetJsError = () => {
    setShowJsError(false)
    setJsErrorType('')
  }

  const triggerUnhandledPromiseRejection = () => {
    // This won't be caught by ErrorBoundary but will show in console
    Promise.reject(new Error('Unhandled promise rejection'))
  }

  const triggerConsoleError = () => {
    console.error('Manual console error', { 
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent 
    })
    toast.error('Check console for error details')
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-4">Error Handling Test Page</h1>
        <p className="text-gray-600 mb-4">
          Use this page to manually test different error scenarios in your application.
          Open browser DevTools to see console errors and network failures.
        </p>
      </div>

      {/* JavaScript Error Testing */}
      <div className="mb-8 p-6 border rounded-lg">
        <h2 className="text-xl font-semibold mb-4">1. JavaScript Errors (ErrorBoundary)</h2>
        <p className="text-gray-600 mb-4">
          These errors will be caught by the ErrorBoundary and show the error screen.
        </p>
        
        <div className="flex gap-2 mb-4 flex-wrap">
          <Button onClick={() => triggerJsError('reference')} variant="destructive">
            Trigger Reference Error
          </Button>
          <Button onClick={() => triggerJsError('type')} variant="destructive">
            Trigger Type Error
          </Button>
          <Button onClick={() => triggerJsError('custom')} variant="destructive">
            Trigger Custom Error
          </Button>
          <Button onClick={resetJsError} variant="outline">
            Reset
          </Button>
        </div>

        {/* This will be wrapped by ErrorBoundary */}
        <div className="border p-4 rounded bg-gray-50">
          <h4 className="font-medium mb-2">Error Test Zone:</h4>
          <ErrorBoundary>
            <ErrorThrower shouldThrow={showJsError} errorType={jsErrorType} />
          </ErrorBoundary>
        </div>
      </div>

      {/* API Error Testing */}
      <div className="mb-8 p-6 border rounded-lg">
        <h2 className="text-xl font-semibold mb-4">2. API Errors</h2>
        <p className="text-gray-600 mb-4">
          These will test your API error handling and show errors in Redux state or toast notifications.
        </p>
        <AsyncErrorTester />
      </div>

      {/* Other Error Testing */}
      <div className="mb-8 p-6 border rounded-lg">
        <h2 className="text-xl font-semibold mb-4">3. Other Error Types</h2>
        <p className="text-gray-600 mb-4">
          Test other types of errors that may not be caught by ErrorBoundary.
        </p>
        
        <div className="flex gap-2 flex-wrap">
          <Button onClick={triggerUnhandledPromiseRejection} variant="destructive">
            Unhandled Promise Rejection
          </Button>
          <Button onClick={triggerConsoleError} variant="destructive">
            Console Error
          </Button>
        </div>
      </div>

      {/* Manual Testing Instructions */}
      <div className="p-6 border rounded-lg bg-blue-50">
        <h2 className="text-xl font-semibold mb-4">4. Manual Testing Tips</h2>
        <div className="space-y-2 text-sm">
          <div>
            <strong>Network Errors:</strong> 
            <ul className="list-disc list-inside ml-4 mt-1">
              <li>Stop your backend server and try logging in</li>
              <li>Use browser DevTools → Network tab → set to "Offline"</li>
              <li>Use DevTools → Network tab → throttle to "Slow 3G"</li>
            </ul>
          </div>
          <div>
            <strong>Invalid Login:</strong>
            <ul className="list-disc list-inside ml-4 mt-1">
              <li>Go to login page and enter wrong credentials</li>
              <li>Try empty email or password fields</li>
            </ul>
          </div>
          <div>
            <strong>Browser DevTools:</strong>
            <ul className="list-disc list-inside ml-4 mt-1">
              <li>Console tab: See JavaScript errors and API failures</li>
              <li>Network tab: See HTTP requests and responses</li>
              <li>Application tab: Check localStorage for auth tokens</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ErrorTestPage